/**
 * Community directory — /community/directory
 *
 * Filterable + searchable member grid. Filters: free-text q (FTS),
 * specialty, region, certified-only. All filters are URL-query-driven
 * for shareable links.
 */
import * as React from "react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  fetchDirectory,
  type DirectoryEntry,
  type DirectoryResponse,
} from "@/lib/community-api";

const PAGE_SIZE = 20;

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: DirectoryResponse }
  | { kind: "not_member" }
  | { kind: "error"; message: string };

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

export interface CommunityDirectoryPageProps {
  fetchFn?: typeof fetchDirectory;
  onUnauthenticated?: (returnTo: string) => void;
}

export function CommunityDirectoryPage({
  fetchFn = fetchDirectory,
  onUnauthenticated = redirectToLogin,
}: CommunityDirectoryPageProps = {}): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const specialty = params.get("specialty") ?? "";
  const region = params.get("region") ?? "";
  const certified = params.get("certified") === "true";
  const offset = Number.parseInt(params.get("offset") ?? "0", 10) || 0;

  const [state, setState] = useState<LoadState>({ kind: "loading" });
  // Locally-buffered filter inputs so typing doesn't slam the server on
  // every keystroke; we sync to URL on form submit.
  const [qInput, setQInput] = useState(q);
  const [specialtyInput, setSpecialtyInput] = useState(specialty);
  const [regionInput, setRegionInput] = useState(region);

  useEffect(() => {
    setQInput(q);
    setSpecialtyInput(specialty);
    setRegionInput(region);
  }, [q, specialty, region]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        setState({ kind: "loading" });
        // exactOptionalPropertyTypes — build the filters object
        // conditionally so we never pass `undefined` for a non-undefined-typed property.
        const filters: Parameters<typeof fetchFn>[0] = {
          certified,
          limit: PAGE_SIZE,
          offset,
        };
        if (q) filters.q = q;
        if (specialty) filters.specialty = specialty;
        if (region) filters.region = region;
        const data = await fetchFn(filters);
        if (cancelled) return;
        setState({ kind: "ready", data });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onUnauthenticated("/community/directory");
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setState({ kind: "not_member" });
          return;
        }
        const msg =
          err instanceof Error
            ? err.message
            : "Could not load the directory.";
        setState({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchFn, q, specialty, region, certified, offset, onUnauthenticated]);

  const applyFilters = (
    nextCertified: boolean | undefined,
    nextOffset: number,
  ): void => {
    const next = new URLSearchParams();
    if (qInput) next.set("q", qInput);
    if (specialtyInput) next.set("specialty", specialtyInput);
    if (regionInput) next.set("region", regionInput);
    const useCertified =
      nextCertified !== undefined ? nextCertified : certified;
    if (useCertified) next.set("certified", "true");
    if (nextOffset > 0) next.set("offset", String(nextOffset));
    setParams(next);
  };

  if (state.kind === "loading") {
    return (
      <section
        className="mx-auto max-w-5xl px-4 py-12 sm:px-6"
        aria-busy="true"
      >
        <p className="text-sm text-muted-foreground" role="status">
          Loading directory…
        </p>
      </section>
    );
  }

  if (state.kind === "not_member") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 text-center">
        <h1 className="text-2xl font-bold text-foreground">Community</h1>
        <p className="mt-4 text-muted-foreground">
          The community is open to active learners and certified
          practitioners.
        </p>
        <Link
          to="/community"
          className="mt-6 inline-flex items-center rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Back to community
        </Link>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <p className="text-sm text-red-700" role="alert">
          {state.message}
        </p>
      </section>
    );
  }

  const { members, total, next_offset } = state.data;

  return (
    <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <nav className="text-xs text-muted-foreground">
        <Link to="/community" className="hover:text-foreground">
          Community
        </Link>{" "}
        <span aria-hidden="true">›</span> Directory
      </nav>

      <header className="mt-3 mb-8">
        <h1 className="text-3xl font-bold text-foreground">Directory</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Listed members of the community. Update your visibility in{" "}
          <Link to="/community/me/profile" className="underline">
            My profile
          </Link>{" "}
          to be listed here.
        </p>
      </header>

      <form
        className="space-y-3 rounded-lg border border-border bg-muted/30 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          applyFilters(undefined, 0);
        }}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label htmlFor="dir-q" className="block text-xs font-medium">
              Search
            </label>
            <input
              id="dir-q"
              type="search"
              value={qInput}
              onChange={(e) => {
                setQInput(e.target.value);
              }}
              placeholder="bio, handle, specialty…"
              className="mt-1 block w-full rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label
              htmlFor="dir-specialty"
              className="block text-xs font-medium"
            >
              Specialty
            </label>
            <input
              id="dir-specialty"
              type="text"
              value={specialtyInput}
              onChange={(e) => {
                setSpecialtyInput(e.target.value);
              }}
              placeholder="e.g. ptsd"
              className="mt-1 block w-full rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="dir-region" className="block text-xs font-medium">
              Region
            </label>
            <input
              id="dir-region"
              type="text"
              value={regionInput}
              onChange={(e) => {
                setRegionInput(e.target.value);
              }}
              placeholder="e.g. NC"
              className="mt-1 block w-full rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={certified}
              onChange={(e) => {
                applyFilters(e.target.checked, 0);
              }}
            />
            Certified only
          </label>
          <span className="flex-1" />
          <button
            type="submit"
            className="rounded-md bg-primary px-3 py-1 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Apply
          </button>
        </div>
      </form>

      {members.length === 0 ? (
        <p className="mt-12 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No members match those filters yet.
        </p>
      ) : (
        <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {members.map((m) => (
            <li key={m.user_id}>
              <MemberCard member={m} />
            </li>
          ))}
        </ul>
      )}

      {(offset > 0 || next_offset !== null) && (
        <nav className="mt-8 flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={() => {
              applyFilters(undefined, Math.max(0, offset - PAGE_SIZE));
            }}
            disabled={offset === 0}
            className="rounded-md border border-border px-3 py-1 hover:bg-muted disabled:opacity-50"
          >
            ← Previous
          </button>
          <p className="text-xs text-muted-foreground">
            {offset + 1}–{Math.min(offset + members.length, total)} of {total}
          </p>
          <button
            type="button"
            onClick={() => {
              if (next_offset !== null) {
                applyFilters(undefined, next_offset);
              }
            }}
            disabled={next_offset === null}
            className="rounded-md border border-border px-3 py-1 hover:bg-muted disabled:opacity-50"
          >
            Next →
          </button>
        </nav>
      )}
    </section>
  );
}

function MemberCard({
  member,
}: {
  member: DirectoryEntry;
}): React.JSX.Element {
  const target = member.handle ?? member.user_id;
  return (
    <Link
      to={`/community/members/${encodeURIComponent(target)}`}
      className="block h-full rounded-lg border border-border bg-white p-4 hover:border-primary hover:shadow-sm transition-all"
    >
      <div className="flex items-baseline gap-2">
        <h3 className="text-base font-semibold text-foreground">
          {member.display_name}
        </h3>
        {member.credential_visible && member.is_certified && (
          <span
            className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"
            title="Certified"
          >
            ✓ Certified
          </span>
        )}
      </div>
      {member.handle && (
        <p className="mt-0.5 text-xs text-muted-foreground">@{member.handle}</p>
      )}
      {member.headline && (
        <p className="mt-2 text-sm text-foreground">{member.headline}</p>
      )}
      {member.region && (
        <p className="mt-2 text-xs text-muted-foreground">{member.region}</p>
      )}
      {member.specialties.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-1">
          {member.specialties.slice(0, 5).map((s) => (
            <li
              key={s}
              className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground"
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </Link>
  );
}
